import React, { useEffect, useState } from 'react';

import { Card } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Remove } from '@material-ui/icons';
import { Button } from 'react-bootstrap';
import { shallowEqual, useSelector } from 'react-redux';

import { useSubheader } from '../../../_metronic/layout';
import { onDemandPaymentFields } from '../../../airtable/metadata/payments';
import { fetchOnDemandPayments } from '../../../airtable/on-demand-payments';
import { formatCurrency } from '../../../commons/utils';
import { createCheckoutSession } from '../../../stripe';
import { createColumn } from '../../components/dynamic-table';
import { ExpandableTable } from '../../components/expandable-table';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    marginBottom: '1rem',
  },
  status: {
    marginBottom: '2rem',
  },
  submitButton: {
    marginLeft: '5px',
    width: '100px',
  },
  toolbar: {
    backgroundColor: 'white',
    display: 'flex',
    justifyContent: 'space-between',
  },
}));

const commonColumns = [
  createColumn(onDemandPaymentFields.PAYMENT_NUMBER, { type: 'numeric' }),
  createColumn(onDemandPaymentFields.TITLE, { type: 'string' }),
  createColumn(onDemandPaymentFields.AMOUNT, {
    render: payment => formatCurrency(payment.fields[onDemandPaymentFields.AMOUNT]),
    type: 'numeric',
  }),
];

export function OnDemandPaymentsPage() {
  const classes = useStyles();
  const user = useSelector(({ auth }) => auth.user, shallowEqual);
  const subheader = useSubheader();
  subheader.setTitle('On-demand payments');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    async function fetchAll() {
      setRows(await fetchOnDemandPayments(user));
      setLoading(false);
    }
    void fetchAll();
  }, [user]);

  async function payWithStripe(payment) {
    setLoading(true);

    try {
      const session = await createCheckoutSession({
        target: payment.id,
        type: 'on-demand-payment',
        user,
      });

      if (session.url) {
        window.location.href = session.url;
        return true;
      }

      return false;
    } catch (error) {
      const text = error.response?.data?.raw
        ? `${error.message}.\nStripe returned this message: ${error.response.data.raw.message}\nStripe request ID: ${error.response.data.raw.requestId}`
        : error.response?.data?.message
          ? `${error.message}: ${error.response.data.message}`
          : error.message;

      setStatus({ error: true, text });
    } finally {
      setLoading(false);
    }
  }

  const columns = [
    ...commonColumns,
    createColumn('Action', {
      align: 'right',
      colSpan: 1,
      size: 'small',
      sortable: false,
      render: payment => {
        if (payment.fields[onDemandPaymentFields.STRIPE_ID]) {
          return <Remove />;
        }

        return <Button
          className={classes.submitButton}
          disabled={loading}
          onClick={event => {
            void payWithStripe(payment);
            event.stopPropagation();
          }}
          type="button"
          variant="primary"
        >
          <span>Pay</span>
          {loading && <span className="ml-3 spinner spinner-white" />}
        </Button>;
      },
    }),
  ];

  return (
    <div className={classes.root}>
      {status && (
        <div className={`alert alert-custom alert-dismissible alert-light-${status.error ? 'danger' : 'success'} ${classes.status}`}>
          <div className="alert-text font-weight-bold" style={{ whiteSpace: 'pre-line' }}>
            {status.text}
          </div>
        </div>
      )}

      <Card className={classes.card}>
        <ExpandableTable columns={columns} rows={rows}>
          {row => (
            <span style={{ whiteSpace: 'pre-line' }}>
              {row.fields[onDemandPaymentFields.DESCRIPTION]}
            </span>
          )}
        </ExpandableTable>
      </Card>
    </div>
  );
}
